import React, { useState, useMemo } from 'react';
import SpeakerCard from './SpeakerCard';
import Header from '../shared/Header';
import SearchBox from '../ui-components/SearchBox';
import SpeakerModal from '../shared/SpeakerModal';
import { useModalStack } from '../hooks/useModalStack.js';
import { useOpenRecordFromUrl, URL_RECORD_PARAMS } from '../hooks/useOpenRecordFromUrl.js';
import { useAddToCalendarScript } from '../hooks/useAddToCalendarScript.js';
import { MODAL_TYPES, WIDGET_TYPES } from '../constants.js';
import getVisibleFieldIdsOfType, { getVisibleFieldsOfTypeById } from '../utils/get-visible-fields-of-widget.js';
import Footer from '../shared/Footer.jsx';
import NoResults from '../shared/NoResults.jsx';
import SessionModal from '../shared/SessionModal.jsx';
import Loading from '../shared/Loading.jsx';
import { orderSpeakersForEmbed } from '../utils/embed-sort.js';
import SubsessionModal from '../shared/SubsessionModal.jsx';
import { getSubsessionDataForModal } from '../utils/subsession-modal.js';
import { matchParticipant } from '../utils/search.js';
import { useI18n } from '../i18n/index.jsx';

const getSpeakerModalData = (speakers, sessionsById, speakerIdModal) => {
  const speakerModalData = speakers.find(speaker => speaker.id === speakerIdModal);
  return {
    ...speakerModalData,
    sessions: (speakerModalData?.sessions || []).map(session => sessionsById[session.id]),
  };
};

const getSessionDataForModal = (sessionId, sessionsById, speakersById = {}) => {
  const sessionData = sessionsById[sessionId];
  return {
    ...sessionData,
    timezone: sessionData?.timezone,
    // SB-10242 audit: SessionModal reads `ceuCredits`, not the raw `ceu_credits`.
    ceuCredits: sessionData?.ceu_credits,
    tags: (sessionData?.tags || []).map(tag => tag.name).join(', '),
    tagsList: (sessionData?.tags || []).map(tag => tag.name),
    speakers: (sessionData?.speakers || []).map(speaker => ({
      ...speaker,
      name: speaker.full_name || speaker.first_name + ' ' + speaker.last_name,
      salutation: speaker.salutation || speakersById[speaker.id]?.salutation || '',
      honorific: speaker.honorific || speakersById[speaker.id]?.honorific || '',
      role: speaker.role || 'speaker',
    })),
    // Calendar data if available
    endTimeAddEvent: sessionData?.end_time_addEvent,
    endsAtAddEvent: sessionData?.ends_at_addEvent,
    startTimeAddEvent: sessionData?.start_time_addEvent,
    startsAtAddEvent: sessionData?.starts_at_addEvent,
  };
};

/**
 * @param {import('../types/async-data.js').AsyncDataResponse<import('../types/async-data.js').SpeakerScopeData>} payload
 * @param {boolean} loading
 */
const SpeakersGallery = ({ data: payload, loading = false }) => {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const { visibleModal, openModal, closeModal } = useModalStack();
  const searchSpeakersEnabled = payload?.data?.filterOptions?.search_speaker_session_by_name || false;
  const useModals = payload?.useModals || false;
  const hideSessionDetails = payload?.hideSessionDetails || false;
  const sortByFields = payload?.sortByFields || [];
  const sortedSpeakers = orderSpeakersForEmbed(
    payload?.data?.speakers || [],
    sortByFields,
    WIDGET_TYPES.SPEAKERS_GALLERY
  );
  const sessionsById = payload?.data?.sessionsById || {};
  const speakersById = Object.fromEntries(sortedSpeakers.map(speaker => [speaker.id, speaker]));

  useOpenRecordFromUrl({
    ready: !loading && useModals,
    shortcuts: [
      {
        param: URL_RECORD_PARAMS.SPEAKER,
        modalType: MODAL_TYPES.SPEAKER,
        // speakers is a plain array (no keyed map in this payload)
        exists: id => sortedSpeakers.some(speaker => String(speaker.id) === id),
      },
    ],
    openModal,
  });
  const eventName = payload?.eventName;
  const primaryColor = payload?.primaryColor;
  const isDarkTheme = payload?.isDarkTheme || false;
  const groupedFields = payload?.groupedFields || {};
  const showAddToCalendar = payload?.data?.filterOptions?.show_add_to_calendar || false;
  const { isLoaded: isAddToCalendarScriptLoaded } = useAddToCalendarScript(showAddToCalendar);

  const visibleSpeakerFields = getVisibleFieldIdsOfType(groupedFields, WIDGET_TYPES.SPEAKERS);
  const visibleSpeakerFieldsById = getVisibleFieldsOfTypeById(groupedFields, WIDGET_TYPES.SPEAKERS);
  const visibleSessionFields = getVisibleFieldIdsOfType(groupedFields, WIDGET_TYPES.SESSIONS);
  const visibleSessionFieldsById = getVisibleFieldsOfTypeById(groupedFields, WIDGET_TYPES.SESSIONS);

  const speakerData = getSpeakerModalData(sortedSpeakers, sessionsById, visibleModal?.id);

  const filteredSpeakers = useMemo(() => {
    if (!searchTerm.trim()) {
      return sortedSpeakers;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    // SB-8854: one participant field set across both speaker embeds — the
    // gallery was missing bio, which the speakers embed has matched since
    // SB-6071.
    return sortedSpeakers.filter(speaker => matchParticipant(speaker, lowerSearchTerm));
  }, [sortedSpeakers, searchTerm]);

  const handleSpeakerClick = speakerId => {
    openModal(MODAL_TYPES.SPEAKER, speakerId);
  };

  const handleCloseModal = (type, id) => {
    closeModal(type, id);
  };

  const handleSessionClick = sessionId => {
    openModal(MODAL_TYPES.SESSION, sessionId);
  };

  // SB-8856: subsession rows push a subsession detail modal.
  const handleSubsessionClick = subsessionId => {
    openModal(MODAL_TYPES.SUBSESSION, subsessionId);
  };

  // Handle cases where data might be null/undefined or loading
  if (!payload && loading) {
    return (
      <div className="speakers-gallery__loading">
        <Loading size="large" colorVariant="primary" text={t('loading_data')} />
      </div>
    );
  }

  return (
    <div
      className={`speakers-gallery ${isDarkTheme ? 'theme-dark' : ''}`}
      style={primaryColor ? { '--color-primary': primaryColor } : {}}
    >
      <Header eventName={eventName} primaryColor={primaryColor} hidden={Boolean(payload?.hideHeader)} />

      <main className="speakers__main">
        <div className="speakers__content">
          <div className="speakers__page-header">
            <h2 className="speakers__title">{t('role_speakers')}</h2>
            <div className="speakers__search-section">
              {searchSpeakersEnabled && (
                <SearchBox placeholder={t('search_placeholder_speaker_name')} value={searchTerm} onChange={setSearchTerm} />
              )}
            </div>
          </div>

          <div className="speakers__grid-container">
            {filteredSpeakers.length === 0 ? (
              <NoResults
                widgetItem={t('items_speakers')}
                searchQuery={searchTerm}
                hasActiveFiltersOrSearch={searchTerm.trim()}
                handleClearFilters={() => setSearchTerm('')}
              />
            ) : (
              <div className="speakers__grid">
                {filteredSpeakers.map(speaker => (
                  <SpeakerCard key={speaker.id} speaker={speaker} onClick={() => handleSpeakerClick(speaker.id)} visibleSpeakerFields={visibleSpeakerFields} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {useModals && visibleModal?.type === MODAL_TYPES.SPEAKER && (
        <SpeakerModal
          speakerData={speakerData}
          visibleSpeakerFields={visibleSpeakerFields}
          visibleSpeakerFieldsById={visibleSpeakerFieldsById}
          onClose={() => handleCloseModal(MODAL_TYPES.SPEAKER, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SPEAKER, visibleModal.id)}
          onSessionClick={handleSessionClick}
          hideSessionDetails={hideSessionDetails}
        />
      )}

      {useModals && visibleModal?.type === MODAL_TYPES.SESSION && (
        <SessionModal
          visibleSpeakerFields={visibleSpeakerFields}
          sessionData={getSessionDataForModal(visibleModal.id, sessionsById, speakersById)}
          visibleSessionFields={visibleSessionFields}
          visibleSessionFieldsById={visibleSessionFieldsById}
          onClose={() => handleCloseModal(MODAL_TYPES.SESSION, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SESSION, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
          onSubsessionClick={handleSubsessionClick}
          showAddToCalendar={showAddToCalendar && isAddToCalendarScriptLoaded}
        />
      )}

      {useModals && visibleModal?.type === MODAL_TYPES.SUBSESSION && (
        <SubsessionModal
          visibleSpeakerFields={visibleSpeakerFields}
          subsessionData={getSubsessionDataForModal(visibleModal.id, sessionsById, speakersById)}
          visibleSessionFields={visibleSessionFields}
          visibleSessionFieldsById={visibleSessionFieldsById}
          onClose={() => handleCloseModal(MODAL_TYPES.SUBSESSION, visibleModal.id)}
          onBack={() => handleCloseModal(MODAL_TYPES.SUBSESSION, visibleModal.id)}
          onSpeakerClick={handleSpeakerClick}
        />
      )}

      {/* Footer */}
      <Footer hidden={Boolean(payload?.hideFooter)} />
    </div>
  );
};

export default SpeakersGallery;
